import { Resource, ResourceMetadata, ResourceRepository, ResourceTypes } from '@telespot/domain';
import { IFileUploader } from '@shared/use-cases';

export interface SaveResourceInput {
  id?: string;
  name: string;
  type: string;
  version: number;
  description: string;
  value: string;
  displayName?: Record<string, string>;
  metadata?: Record<string, unknown>;
  childIds: string[];
  file?: File;
}

export interface SaveResourceOutput {
  resourceId: string;
}

export enum SaveResourceErrorCode {
  MISSING_NAME = 'A non-empty `name` is required to save a resource.',
  MISSING_TYPE = 'A resource type is required to save a resource.',
  UPLOAD_FAILED = 'Uploading the resource file failed.',
  MISSING_PERSISTED_ID = 'ResourceRepository.save() returned an empty id.',
}

export class SaveResourceException extends Error {
  constructor(public readonly code: SaveResourceErrorCode) {
    super(code.toString());
    this.name = 'SaveResourceException';
    Object.setPrototypeOf(this, SaveResourceException.prototype);
  }
}

export interface ISaveResourceCommand {
  execute(input: SaveResourceInput): Promise<SaveResourceOutput>;
}

export class SaveResourceCommand implements ISaveResourceCommand {
  constructor(
    private readonly resourceRepository: ResourceRepository,
    private readonly fileUploader: IFileUploader
  ) {}

  private childStub(id: string): Resource {
    // Only `id` matters: ParseResourceMapper.fromDomain persists nested
    // resources as references via `createWithoutData(r.id)`.
    return new Resource({
      id,
      name: '',
      type: ResourceTypes.mask,
      resources: [],
      version: 1,
      description: '',
      value: '',
    });
  }

  public async execute(input: SaveResourceInput): Promise<SaveResourceOutput> {
    if (!input.name?.trim()) {
      throw new SaveResourceException(SaveResourceErrorCode.MISSING_NAME);
    }
    if (!input.type) {
      throw new SaveResourceException(SaveResourceErrorCode.MISSING_TYPE);
    }

    let value = input.value;
    if (input.file) {
      try {
        value = await this.fileUploader.upload(input.file, 'support-files');
      } catch {
        throw new SaveResourceException(SaveResourceErrorCode.UPLOAD_FAILED);
      }
    }

    const resources =
      input.type === ResourceTypes.mask ? input.childIds.map((id) => this.childStub(id)) : [];

    const resource = new Resource({
      id: input.id,
      name: input.name,
      type: input.type as ResourceTypes,
      version: input.version,
      description: input.description,
      value,
      metadata: input.metadata as ResourceMetadata | undefined,
      displayName: input.displayName,
      resources,
    });

    const id = await this.resourceRepository.save(resource);
    if (!id) {
      throw new SaveResourceException(SaveResourceErrorCode.MISSING_PERSISTED_ID);
    }
    return { resourceId: id };
  }
}
